import { Box, Chip, Tooltip } from "@mui/material";
import { grey } from "@mui/material/colors";
import { BentoCategoriesContext } from "contexts/BentoCategories";
import { ReactNode, useContext } from "react";
import { BentoBrandSearchParameters } from "schemas/dashboard";

import { getTagLabelFromParams, parseQuery } from "utils/discoverSearch";
import { formatHugeNumbers } from "utils/string";

interface Props {
  searchParameters?: BentoBrandSearchParameters;
  maxVisible?: number;
  emptyFallback?: ReactNode;
}

interface FilterChip {
  label: string;
  value: string;
}

const TAG_FIELDS: { key: string; label: string }[] = [
  { key: "company_types", label: "Type" },
  { key: "contact_types", label: "Contact" },
  { key: "locations", label: "Location" },
  { key: "compensation_types", label: "Compensation" },
  { key: "languages", label: "Language" },
  { key: "target_genders", label: "Gender" },
];

const formatRange = (
  min: number | undefined,
  max: number | undefined,
  format: (n: number) => string,
) => {
  if (min && max) return `${format(min)} – ${format(max)}`;
  if (min) return `${format(min)}+`;
  if (max) return `< ${format(max)}`;
  return null;
};

const CampaignFilterChips = ({
  searchParameters,
  maxVisible = 3,
  emptyFallback,
}: Props) => {
  const { categoriesById } = useContext(BentoCategoriesContext);

  if (!searchParameters) return emptyFallback ? <>{emptyFallback}</> : null;

  const chips: FilterChip[] = [];

  if (searchParameters.query) {
    const { include, exclude } = parseQuery(searchParameters.query);
    if (include?.length) {
      chips.push({ label: "Query", value: include.join(", ") });
    }
    if (exclude?.length) {
      chips.push({ label: "Exclude", value: exclude.join(", ") });
    }
  }

  if (searchParameters.category_ids?.length) {
    const names = searchParameters.category_ids
      .map((id) => categoriesById[parseInt(id)]?.name)
      .filter(Boolean);
    if (names.length) {
      chips.push({ label: "Categories", value: names.join(", ") });
    } else {
      chips.push({
        label: "Categories",
        value: `${searchParameters.category_ids.length} selected`,
      });
    }
  }

  for (const { key, label } of TAG_FIELDS) {
    const labels = getTagLabelFromParams(key, searchParameters);
    if (labels.length) chips.push({ label, value: labels.join(", ") });
  }

  const igRange = formatRange(
    searchParameters.min_instagram_followers,
    searchParameters.max_instagram_followers,
    formatHugeNumbers,
  );
  if (igRange) chips.push({ label: "IG Followers", value: igRange });

  const ageRange = formatRange(
    searchParameters.min_target_age,
    searchParameters.max_target_age,
    String,
  );
  if (ageRange) chips.push({ label: "Age", value: ageRange });

  if (chips.length === 0) return emptyFallback ? <>{emptyFallback}</> : null;

  const visible = chips.slice(0, maxVisible);
  const hidden = chips.slice(maxVisible);

  return (
    <Box
      sx={{
        display: "flex",
        gap: 0.5,
        flexWrap: "wrap",
        mt: 0.75,
        alignItems: "center",
      }}
    >
      <Tooltip
        title="Search filters used to build this campaign"
        placement="top"
      >
        <Box
          component="i"
          className="fa-regular fa-filter"
          sx={{ fontSize: 11, color: grey[500], mr: 0.25 }}
        />
      </Tooltip>
      {visible.map((chip) => (
        <Tooltip key={chip.label} title={`${chip.label}: ${chip.value}`}>
          <Chip
            size="small"
            label={`${chip.label}: ${chip.value}`}
            sx={{
              maxWidth: 220,
              fontSize: 11,
              height: 22,
              backgroundColor: grey[100],
              color: grey[800],
              border: `1px solid ${grey[200]}`,
              "& .MuiChip-label": {
                px: 1,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              },
            }}
          />
        </Tooltip>
      ))}
      {hidden.length > 0 && (
        <Tooltip
          title={
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
              {hidden.map((c) => (
                <span key={c.label}>
                  {c.label}: {c.value}
                </span>
              ))}
            </Box>
          }
        >
          <Chip
            size="small"
            label={`+${hidden.length}`}
            sx={{
              fontSize: 11,
              height: 22,
              backgroundColor: grey[100],
              color: grey[700],
              border: `1px solid ${grey[200]}`,
            }}
          />
        </Tooltip>
      )}
    </Box>
  );
};

export default CampaignFilterChips;
